/**
 * Wheel Share Video — end-card pure logic (C11, SPEC §7 + §8.2 `cta`).
 *
 * The deterministic, framework-free core for the video's END-CARD: the mandatory
 * outro scene that turns a passive viewer back into an active one (SPEC §7
 * "drive-back guarantee"). It carries a short CTA line, the Wheel's public link,
 * and an optional QR sticker of that link. No React / network / DOM here — so the
 * config math is exhaustively unit-testable and both the config UI (EndCardSection)
 * and the render-payload builder (shareVideo.buildRequestPayload) consume the SAME
 * normalized model.
 *
 * This unit is the FE CONFIG + the end-card DATA the render POST body carries; the
 * actual BAKE of the QR/CTA into the MP4 is render-side (a later, gated follow-on).
 *
 * Two guards baked in here (one place, tested):
 *  1. CTA is short + length-capped (SPEC §7 "a short call") so a long CTA can't
 *     blow the end-card layout; a blank CTA falls back to the sane default.
 *  2. LINK is validated as an http(s) URL. A blank/invalid link disables QR (you
 *     can't encode nothing) and, in the payload, the cta simply carries no link —
 *     never a bogus `qr:true` with no link to point at.
 */

// SPEC §7: the mandatory end-card's default call ("See the full Wheel →"). The
// arrow is a render-side decoration; the stored/config text is the clean phrase.
export const DEFAULT_CTA_TEXT = "See the full Wheel";

// A short call, per SPEC §7. Capped so a pasted paragraph can't overflow the
// end-card's safe area. The PAYLOAD carries the capped value (the render bakes
// exactly what the user saw), so the cap is the single source of truth.
export const MAX_CTA_LENGTH = 40;

/**
 * Normalize a CTA string: trim, collapse to the sane default when blank, and cap
 * to MAX_CTA_LENGTH (appending a single ellipsis when clipped). Pure + total.
 */
export function normalizeCtaText(value) {
  if (value === null || value === undefined) return DEFAULT_CTA_TEXT;
  const s = String(value).trim();
  if (!s) return DEFAULT_CTA_TEXT;
  if (s.length <= MAX_CTA_LENGTH) return s;
  // Reserve one slot for the ellipsis so the result is exactly MAX_CTA_LENGTH.
  return `${s.slice(0, MAX_CTA_LENGTH - 1).trimEnd()}…`;
}

/**
 * True when `link` is a usable end-card link: a syntactically valid absolute
 * http(s) URL. Rejects blanks, non-strings, relative paths, and non-http schemes
 * (mailto:, javascript:, data:, …). This is what gates the QR — you can only
 * encode a real link.
 */
export function isValidLink(link) {
  if (link === null || link === undefined) return false;
  const s = String(link).trim();
  if (!s) return false;
  let u;
  try {
    u = new URL(s);
  } catch (e) {
    return false;
  }
  return u.protocol === "http:" || u.protocol === "https:";
}

/**
 * Resolve the link the end-card should use: the user's explicit link when it is a
 * valid http(s) URL; otherwise fall back to the Wheel's own share URL when THAT is
 * valid; otherwise "" (no link — a private Wheel with no public URL yet). Pure.
 */
export function resolveLink(userLink, defaultLink) {
  if (isValidLink(userLink)) return String(userLink).trim();
  if (isValidLink(defaultLink)) return String(defaultLink).trim();
  return "";
}

/**
 * Build the normalized end-card model the UI renders around and the payload is
 * derived from.
 *
 * @param {object} input
 * @param {string} [input.ctaText]     the user's CTA text (blank => default)
 * @param {string} [input.link]        the user's link (invalid => defaultLink)
 * @param {boolean} [input.qr]         whether the user asked for a QR sticker
 * @param {string} [input.defaultLink] the Wheel's own share URL (the fallback)
 * @returns {{ enabled:boolean, label:string, link:string, hasLink:boolean,
 *             qr:boolean, linkInvalid:boolean }}
 *   `qr` is coerced to false when there is no valid link (can't encode nothing).
 *   `linkInvalid` is true only when the user TYPED something that isn't a valid
 *   link (so the UI can show a guard) — a blank field is not "invalid".
 */
export function buildEndCardModel({ ctaText, link, qr, defaultLink } = {}) {
  const label = normalizeCtaText(ctaText);
  const resolved = resolveLink(link, defaultLink);
  const hasLink = resolved !== "";
  // The user typed a non-blank value that didn't validate AND we couldn't fall
  // back to a valid default => surface the invalid-link guard.
  const typed =
    link !== null && link !== undefined && String(link).trim() !== "";
  const linkInvalid = typed && !isValidLink(link) && !isValidLink(defaultLink);
  return {
    enabled: true, // the end-card is mandatory (SPEC §7) whenever this unit is on
    label,
    link: resolved,
    hasLink,
    qr: Boolean(qr) && hasLink, // no link => no QR, always
    linkInvalid,
  };
}

/**
 * Assemble the `cta` sub-object for the render POST body (SPEC §8.2) so a later
 * render bakes the end-card. Snake_case-free here — `cta` keys in §8.2 are
 * already flat (`enabled`, `label`, `link`, `qr`). Emits `link` ONLY when there is
 * a real one, and `qr` is always a boolean already gated by hasLink. Returns
 * `undefined` when disabled, so a caller can omit it and stay byte-identical.
 *
 * @param {object} model a buildEndCardModel() result
 */
export function endCardToPayload(model) {
  if (!model || !model.enabled) return undefined;
  const cta = {
    enabled: true,
    label: model.label,
    qr: Boolean(model.qr),
  };
  if (model.link) cta.link = model.link;
  return cta;
}

/**
 * Convenience: raw inputs -> render-payload `cta` sub-object in one call. Used by
 * buildRequestPayload so callers pass `{ ctaText, link, qr, defaultLink }` once.
 */
export function buildEndCardPayload(input) {
  return endCardToPayload(buildEndCardModel(input || {}));
}
